import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const PainPointsSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [visibleCards, setVisibleCards] = useState([]);

  const translations = {
    en: {
      title: "Manufacturing Challenges Costing You Profits",
      subtitle: "Indian aluminum manufacturers lose ₹2.3 lakhs monthly due to these common operational inefficiencies",
      painPoints: [
        {
          icon: "FileText",
          title: "Manual Order Tracking",
          description: "Paper-based systems lead to 23% order delays and customer complaints",
          stat: "23% Order Delays",
          impact: "₹45,000 monthly loss"
        },
        {
          icon: "AlertTriangle",
          title: "Quality Control Failures",
          description: "Inconsistent quality checks result in 15% rejection rates and rework costs",
          stat: "15% Rejection Rate",
          impact: "₹67,000 monthly loss"
        },
        {
          icon: "Package",
          title: "Inventory Discrepancies",
          description: "Manual stock tracking causes 18% inventory variance and production delays",
          stat: "18% Variance",
          impact: "₹38,000 monthly loss"
        },
        {
          icon: "Clock",
          title: "Production Delays",
          description: "Poor scheduling and resource planning leads to 28% delivery delays",
          stat: "28% Late Deliveries",
          impact: "₹52,000 monthly loss"
        }
      ]
    },
    hi: {
      title: "निर्माण चुनौतियां जो आपके मुनाफे को नुकसान पहुंचा रही हैं",
      subtitle: "भारतीय एल्यूमीनियम निर्माता इन सामान्य परिचालन अक्षमताओं के कारण मासिक ₹2.3 लाख खो देते हैं",
      painPoints: [
        {
          icon: "FileText",
          title: "मैन्युअल ऑर्डर ट्रैकिंग",
          description: "कागज-आधारित सिस्टम 23% ऑर्डर देरी और ग्राहक शिकायतों का कारण बनता है",
          stat: "23% ऑर्डर देरी",
          impact: "₹45,000 मासिक नुकसान"
        },
        {
          icon: "AlertTriangle",
          title: "गुणवत्ता नियंत्रण विफलताएं",
          description: "असंगत गुणवत्ता जांच के परिणामस्वरूप 15% अस्वीकृति दर और पुनर्कार्य लागत",
          stat: "15% अस्वीकृति दर",
          impact: "₹67,000 मासिक नुकसान"
        },
        {
          icon: "Package",
          title: "इन्वेंटरी विसंगतियां",
          description: "मैन्युअल स्टॉक ट्रैकिंग 18% इन्वेंटरी भिन्नता और उत्पादन देरी का कारण बनती है",
          stat: "18% भिन्नता",
          impact: "₹38,000 मासिक नुकसान"
        },
        {
          icon: "Clock",
          title: "उत्पादन देरी",
          description: "खराब शेड्यूलिंग और संसाधन योजना 28% डिलीवरी देरी का कारण बनती है",
          stat: "28% देर से डिलीवरी",
          impact: "₹52,000 मासिक नुकसान"
        }
      ]
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }

    // Animate cards on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleCards(prev => [...prev, index]);
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = document.querySelectorAll('.pain-point-card');
    cards.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 bg-surface-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Pain Points Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.painPoints.map((point, index) => (
            <div
              key={index}
              data-index={index}
              className={`pain-point-card bg-white rounded-xl p-6 shadow-card border border-border hover:shadow-lg transition-all duration-500 ${
                visibleCards.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Icon */}
              <div className="flex items-center justify-center w-16 h-16 bg-error-50 rounded-xl mb-6">
                <Icon 
                  name={point.icon} 
                  size={32} 
                  color="var(--color-error)" 
                  strokeWidth={1.5}
                />
              </div>

              {/* Content */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-primary">
                  {point.title}
                </h3>
                
                <p className="text-text-secondary text-sm leading-relaxed">
                  {point.description}
                </p>

                {/* Statistics */}
                <div className="space-y-2 pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-error">
                      {point.stat}
                    </span>
                    <Icon name="TrendingDown" size={16} color="var(--color-error)" />
                  </div>
                  
                  <div className="text-lg font-bold text-primary">
                    {point.impact}
                  </div>
                </div>
              </div>

              {/* Animated Progress Bar */}
              <div className="mt-6">
                <div className="w-full bg-surface-200 rounded-full h-2">
                  <div 
                    className="bg-error h-2 rounded-full transition-all duration-1000 ease-out"
                    style={{
                      width: visibleCards.includes(index) ? 
                        `${[23, 15, 18, 28][index]}%` : '0%'
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Total Impact Summary */}
        <div className="mt-16 bg-white rounded-2xl p-8 shadow-lg border border-border">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <Icon name="AlertCircle" size={32} color="var(--color-error)" />
              <h3 className="text-2xl font-bold text-primary">
                {currentLanguage === 'en' ? 'Total Monthly Impact' : 'कुल मासिक प्रभाव'}
              </h3>
            </div>
            
            <div className="text-4xl font-bold text-error mb-2">
              ₹2,02,000
            </div>
            
            <p className="text-text-secondary">
              {currentLanguage === 'en' ?'Average monthly loss due to operational inefficiencies' :'परिचालन अक्षमताओं के कारण औसत मासिक नुकसान'
              }
            </p>

            {/* Call to Action */}
            <div className="mt-6 p-4 bg-accent-50 rounded-lg">
              <p className="text-accent-700 font-medium">
                {currentLanguage === 'en' ?'Our ERP system eliminates these inefficiencies, saving you ₹1.4+ lakhs monthly' :'हमारा ERP सिस्टम इन अक्षमताओं को समाप्त करता है, आपको मासिक ₹1.4+ लाख बचाता है'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PainPointsSection;